import React, { useEffect, useMemo, useState } from "react";
import { deleteLead, listLeads } from "../leadsApi";
import { Lead } from "../types";
import { SkeletonRow, SummaryCard } from "../components/Skeleton";

type TypeFilter = "all" | Lead["type"];

const TYPE_LABELS: Record<Lead["type"], string> = {
  assessment: "AI 智能匹配",
  self_check: "家庭自查表",
  contact: "联系表单",
};

// Rows shown by the loading skeleton -- picked to roughly match a typical
// result set so the table doesn't visibly resize once real rows swap in.
const SKELETON_ROWS = 8;
// One entry per table column (提交时间/类型/姓名/联系方式/概要/操作).
const SKELETON_WIDTHS = ["w-32", "w-20", "w-20", "w-28", "w-48", "w-10"];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("zh-CN", { hour12: false });
}

// One-line summary of whatever this lead type actually collected, shown in
// the table row so most leads never need expanding to be useful at a glance.
function leadSummary(lead: Lead): string {
  if (lead.type === "self_check") {
    return `已勾选 ${lead.checkedCount ?? 0} 项`;
  }
  if (lead.type === "assessment") {
    const a = lead.answers;
    const parts = [a?.budget, a?.goal, a?.region, a?.timeline].filter(Boolean);
    const top = lead.matches?.[0];
    const matchPart = top ? `· 首选 ${top.country ?? ""} ${top.program ?? ""} ${top.score ?? ""}%` : "";
    return `${parts.join(" / ")} ${matchPart}`.trim();
  }
  return lead.subject || "-";
}

function LeadDetail({ lead }: { lead: Lead }) {
  return (
    <div className="bg-c-gray rounded-lg p-4 text-sm text-secondary-dark/80 flex flex-col gap-3">
      {lead.message && (
        <div>
          <p className="text-xs text-secondary-light mb-1">留言内容</p>
          <p className="whitespace-pre-wrap">{lead.message}</p>
        </div>
      )}
      {lead.answers && (
        <div>
          <p className="text-xs text-secondary-light mb-1">测评答案</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(lead.answers).map(([key, value]) => (
              <span key={key} className="bg-white border border-fourth-light rounded-full px-3 py-1 text-xs">
                {key}: {value}
              </span>
            ))}
          </div>
        </div>
      )}
      {lead.matches && lead.matches.length > 0 && (
        <div>
          <p className="text-xs text-secondary-light mb-1">匹配方案</p>
          <ul className="flex flex-col gap-1">
            {lead.matches.map((m, i) => (
              <li key={i}>
                {m.country} · {m.program} · {m.score}%
              </li>
            ))}
          </ul>
        </div>
      )}
      {lead.selections && Object.keys(lead.selections).length > 0 && (
        <div>
          <p className="text-xs text-secondary-light mb-1">自查勾选（共 {lead.checkedCount ?? 0} 项）</p>
          <div className="flex flex-col gap-1.5">
            {Object.entries(lead.selections).map(([section, items]) => (
              <p key={section}>
                <span className="font-medium">{section}：</span>
                {Array.isArray(items) ? items.join("、") : String(items)}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminLeadsPage() {
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    listLeads()
      .then(setLeads)
      .catch((err) => setError(err instanceof Error ? err.message : "加载失败"));
  }, []);

  async function handleDelete(lead: Lead) {
    if (!window.confirm(`确定要删除「${lead.name}」的留资记录吗？此操作无法撤销。`)) return;
    setDeletingId(lead.id);
    try {
      await deleteLead(lead.id);
      setLeads((prev) => prev?.filter((l) => l.id !== lead.id) ?? prev);
      if (expandedId === lead.id) setExpandedId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "删除失败");
    } finally {
      setDeletingId(null);
    }
  }

  const loading = leads === null;

  const summary = useMemo(() => {
    const list = leads ?? [];
    return {
      total: list.length,
      assessment: list.filter((l) => l.type === "assessment").length,
      self_check: list.filter((l) => l.type === "self_check").length,
      contact: list.filter((l) => l.type === "contact").length,
    };
  }, [leads]);

  const filtered = useMemo(() => {
    const list = leads ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((lead) => {
      if (typeFilter !== "all" && lead.type !== typeFilter) return false;
      if (q) {
        const haystack = `${lead.name} ${lead.phone ?? ""} ${lead.email ?? ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [leads, search, typeFilter]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-secondary-dark">客户信息</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="全部留资" value={loading ? null : summary.total} />
        <SummaryCard label="AI 智能匹配" value={loading ? null : summary.assessment} />
        <SummaryCard label="家庭自查表" value={loading ? null : summary.self_check} />
        <SummaryCard label="联系表单" value={loading ? null : summary.contact} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="搜索姓名 / 电话 / 邮箱"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={loading}
          className="border border-fourth-light rounded-lg px-3 py-2 text-sm bg-white min-w-[220px] outline-none focus:border-gold disabled:opacity-60"
        />
        <div className="flex gap-1">
          {(["all", "assessment", "self_check", "contact"] as TypeFilter[]).map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              disabled={loading}
              className={`px-3 py-1.5 rounded-full text-xs transition-colors disabled:opacity-60 ${
                typeFilter === t
                  ? "bg-secondary-dark text-white"
                  : "bg-white border border-fourth-light text-secondary-dark/70 hover:border-gold"
              }`}
            >
              {t === "all" ? "全部" : TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

      <div className="bg-white rounded-xl border border-fourth-light overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-c-gray text-secondary-light text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-medium">提交时间</th>
              <th className="text-left px-4 py-3 font-medium">类型</th>
              <th className="text-left px-4 py-3 font-medium">姓名</th>
              <th className="text-left px-4 py-3 font-medium">联系方式</th>
              <th className="text-left px-4 py-3 font-medium">概要</th>
              <th className="text-left pl-4 pr-6 py-3 font-medium whitespace-nowrap">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <SkeletonRow key={i} widths={SKELETON_WIDTHS} />
              ))}
            {!loading &&
              filtered.map((lead) => {
                const isOpen = expandedId === lead.id;
                const hasDetail = Boolean(
                  lead.message || lead.answers || (lead.matches && lead.matches.length) || lead.selections,
                );
                const isDeleting = deletingId === lead.id;
                return (
                  <React.Fragment key={lead.id}>
                    <tr className="border-t border-fourth-light">
                      <td
                        className={`px-4 py-3 text-secondary-light whitespace-nowrap ${hasDetail ? "cursor-pointer" : ""}`}
                        onClick={() => hasDetail && setExpandedId(isOpen ? null : lead.id)}
                      >
                        {formatDate(lead.createdAt)}
                      </td>
                      <td
                        className={`px-4 py-3 whitespace-nowrap ${hasDetail ? "cursor-pointer" : ""}`}
                        onClick={() => hasDetail && setExpandedId(isOpen ? null : lead.id)}
                      >
                        <span className="bg-gold/10 text-gold text-xs px-2 py-1 rounded-full">
                          {TYPE_LABELS[lead.type]}
                        </span>
                      </td>
                      <td
                        className={`px-4 py-3 font-medium text-secondary-dark whitespace-nowrap ${hasDetail ? "cursor-pointer" : ""}`}
                        onClick={() => hasDetail && setExpandedId(isOpen ? null : lead.id)}
                      >
                        {lead.name}
                      </td>
                      <td
                        className={`px-4 py-3 text-secondary-dark/80 whitespace-nowrap ${hasDetail ? "cursor-pointer" : ""}`}
                        onClick={() => hasDetail && setExpandedId(isOpen ? null : lead.id)}
                      >
                        {[lead.phone, lead.email].filter(Boolean).join(" · ") || "-"}
                      </td>
                      <td
                        className={`px-4 py-3 text-secondary-dark/70 truncate max-w-[320px] ${hasDetail ? "cursor-pointer" : ""}`}
                        onClick={() => hasDetail && setExpandedId(isOpen ? null : lead.id)}
                      >
                        {leadSummary(lead)}
                        {hasDetail && <span className="text-gold text-xs ml-2">{isOpen ? "收起 ▲" : "详情 ▼"}</span>}
                      </td>
                      <td className="pl-4 pr-6 py-3 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => handleDelete(lead)}
                          disabled={isDeleting}
                          className="text-red-600 hover:text-red-700 text-xs disabled:opacity-50"
                        >
                          {isDeleting ? "删除中…" : "删除"}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-fourth-light">
                        <td colSpan={6} className="px-4 py-4">
                          <LeadDetail lead={lead} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-secondary-light">
                  暂无留资记录
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
